import {
  ErrorPage,
  getAppLocation,
  getCookie,
  getOTAP,
  getPathname,
  HttpErrorPage,
  NotificatiesProvider,
  Notificaties2Provider,
  PageLoading,
  SettingsContext,
  useRequestInit,
  withErrorBoundary,
  useFeature
} from "adviesbox-shared";
import { RouteParams } from "adviesbox-shared/utils/types";
import React, { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import useAbortableFetch from "use-abortable-fetch";
import ComponentsOverview from "../ComponentsOverview";
import {
  Medewerker,
  MedewerkerOrganisatieOutput,
  RechtenOutPut,
  Vestiging,
  VestigingenOutput
} from "../.generated/licenties/licentiestypes";
import DossierAanvraagFoutenProvider from "../shared/dossier-aanvraag-fouten/dossier-aanvraag-fouten-provider";
import OpenDossierLogProvider from "../shared/open-dossier-log/open-dossier-log-provider";
import UserDetailsContext from "../shared/user-details/user-details-context";
import ZoekschermAjax from "../zoekscherm/zoekscherm-ajax";
import AuthenticatedAdviesBoxAppWithDrawer from "./AuthenticatedAdviesBoxAppWithDrawer";
import ExterneKoppelingImportAjax from "../import/externekoppeling-import-ajax";
import { DashboardDataProvider } from "../dashboard/new/new-dashboard-context";
import { SemaphoreProvider } from "../dashboard/new/semaphore-context-provider";
//todo: zie #131719
import { UpscopeProvider } from "@mvkdev/upscope-react-16";
import IntercomBoot from './IntercomWrap';

const AuthenticatedAdviesBoxApp = (): ReactElement => {
  const featureNotificaties2 = useFeature("FeatureNotificaties2");
  const featureUpscope = useFeature("FeatureUpscope");
  const featureIntercom = useFeature("FeatureIntercom");

  const settingsContext = useContext(SettingsContext);
  const { setUserDetails } = useContext(UserDetailsContext);
  const { user, settings, requestInit } = useRequestInit();
  const [medewerkerUrl, setMedewerkerUrl] = useState<string | null>(null);
  const [medewerker, setMedewerker] = useState<Medewerker | null>(null);
  const [medewerkerId, setMedewerkerId] = useState<string | null>(null);
  const [organisatieId, setOrganisatieId] = useState<string | null>(null);
  const [rechtenUrl, setRechtenUrl] = useState<string | null>(null);
  const [rechten, setRechten] = useState<string[] | null>(null);
  const [intercomTokenUrl, setIntercomTokenUrl] = useState<string | null>(null);
  const [intercomToken, setIntercomToken] = useState<string | null>(null);
  const [medewerkerVestigingenUrl, setMedewerkerVestigingenUrl] = useState<string | null>(null);
  const [medewerkerVestigingen, setMedewerkerVestigingen] = useState<{ [key: string]: Vestiging } | null>(null);
  const [organisatieVestigingenUrl, setOrganisatieVestigingenUrl] = useState<string | null>(null);
  const [organisatieVestigingen, setOrganisatieVestigingen] = useState<{ [key: string]: Vestiging } | null>(null);
  const [vestigingId, setVestigingId] = useState<string>("");

  const linkToPortal = getAppLocation(window.location.origin, "ADV", "POR", settingsContext.baseUrls);
  const vestigingIdCookie = getCookie(`${getOTAP(window.location.hostname)}_vestigingid`);

  useMemo(() => {
    setMedewerkerUrl(user?.profile ? `${settings.licentiesOrigin}/Medewerkers/current` : null);
  }, [setMedewerkerUrl, user, settings]);

  const medewerkerRequest = useAbortableFetch<MedewerkerOrganisatieOutput>(medewerkerUrl, requestInit);

  useEffect(() => {
    if (
      !medewerkerRequest ||
      medewerkerRequest.loading ||
      medewerkerRequest.error ||
      !medewerkerRequest.data ||
      typeof medewerkerRequest.data === "string" ||
      !medewerkerRequest.data.isValid
    ) {
      setMedewerker(null);
      setMedewerkerId(null);
      setOrganisatieId(null);
      return;
    }

    setMedewerker(medewerkerRequest.data.medewerker);
    setMedewerkerId(medewerkerRequest.data.medewerkerId);
    setOrganisatieId(medewerkerRequest.data.organisatieId);
  }, [medewerkerRequest, setMedewerker, setMedewerkerId]);

  useMemo(() => {
    setRechtenUrl(medewerkerId ? `${settings.licentiesOrigin}/Medewerkers/${medewerkerId}/Rechten` : null);
  }, [setRechtenUrl, settings, medewerkerId]);

  const rechtenRequest = useAbortableFetch<RechtenOutPut>(rechtenUrl, requestInit);

  useEffect(() => {
    if (
      !rechtenRequest ||
      rechtenRequest.loading ||
      rechtenRequest.error ||
      !rechtenRequest.data ||
      typeof rechtenRequest.data === "string" ||
      !rechtenRequest.data.isValid
    ) {
      setRechten(null);
      return;
    }

    setRechten(rechtenRequest.data.rechten);
  }, [setRechten, rechtenRequest]);

  /* istanbul ignore next */
  useMemo(() => {
    setIntercomTokenUrl(user?.profile ? `${settings.licentiesOrigin}/Medewerkers/GetIntercomToken` : null);
  }, [setIntercomTokenUrl, user, settings]);
/* istanbul ignore next */
  const intercomRequest = useAbortableFetch<string>(intercomTokenUrl, requestInit);
/* istanbul ignore next */
  useEffect(() => {
    if (
      !intercomRequest ||
      intercomRequest.loading ||
      intercomRequest.error ||
      !intercomRequest.data ||
      typeof intercomRequest.data === "string" 
    ) {
      setIntercomToken(null);
      return;
    }
/* istanbul ignore next */
    setIntercomToken(intercomRequest.data);
      boot({
        intercomUserJwt: intercomRequest.data["id_token"]
      })
  }, [setIntercomToken, intercomRequest]);

  useMemo(() => {
    setMedewerkerVestigingenUrl(
      medewerkerId ? `${settings.licentiesOrigin}/Medewerkers/${medewerkerId}/Vestigingen` : null
    );
  }, [setMedewerkerVestigingenUrl, medewerkerId, settings]);

  const medewerkerVestigingenRequest = useAbortableFetch<VestigingenOutput>(medewerkerVestigingenUrl, requestInit);

  useEffect(() => {
    if (
      !medewerkerVestigingenRequest ||
      medewerkerVestigingenRequest.loading ||
      medewerkerVestigingenRequest.error ||
      !medewerkerVestigingenRequest.data ||
      typeof medewerkerVestigingenRequest.data === "string" ||
      !medewerkerVestigingenRequest.data.isValid
    ) {
      setMedewerkerVestigingen(null);
      return;
    }

    setMedewerkerVestigingen(medewerkerVestigingenRequest.data.vestigingen);
  }, [setMedewerkerVestigingen, medewerkerVestigingenRequest]);

  useEffect(() => {
    if (!medewerkerId || !organisatieId || !medewerker) {
      return;
    }

    const newUserDetails = {
      ...medewerker,
      medewerkerId: medewerkerId,
      organisatieId: organisatieId
    };

    setUserDetails(newUserDetails);
  }, [setUserDetails, medewerker, medewerkerId, organisatieId]);

  useMemo(() => {
    setOrganisatieVestigingenUrl(
      organisatieId ? `${settings.licentiesOrigin}/Organisatie/${organisatieId}/Vestigingen` : null
    );
  }, [setOrganisatieVestigingenUrl, settings, organisatieId]);

  const organisatieVestigingenRequest = useAbortableFetch<VestigingenOutput>(organisatieVestigingenUrl, requestInit);

  useEffect(() => {
    if (
      !organisatieVestigingenRequest ||
      organisatieVestigingenRequest.loading ||
      organisatieVestigingenRequest.error ||
      !organisatieVestigingenRequest.data ||
      typeof organisatieVestigingenRequest.data === "string" ||
      !organisatieVestigingenRequest.data.isValid
    ) {
      setOrganisatieVestigingen(null);
      return;
    }

    setOrganisatieVestigingen(organisatieVestigingenRequest.data.vestigingen);
  }, [setOrganisatieVestigingen, organisatieVestigingenRequest]);

  useEffect(() => {
    if (typeof vestigingIdCookie === "string") {
      setVestigingId(vestigingIdCookie);
    }
  }, [vestigingIdCookie, setVestigingId]);

  if (!user || !user.profile) {
    return <ErrorPage error={Error("Geen gebruiker of geen rechten toegekend voor deze gebruiker")} data={null} />;
  }

  if (
    !medewerkerRequest?.data ||
    medewerkerRequest.loading ||
    !rechtenRequest?.data ||
    rechtenRequest.loading ||
    !medewerkerVestigingenRequest?.data ||
    medewerkerVestigingenRequest.loading ||
    !organisatieVestigingenRequest?.data ||
    organisatieVestigingenRequest.loading
  ) {
    return <PageLoading />;
  }

  if (medewerkerRequest.error) {
    return <ErrorPage error={medewerkerRequest.error} data={medewerkerRequest.data} />;
  }

  if (rechtenRequest.error) {
    return <ErrorPage error={rechtenRequest.error} data={rechtenRequest.data} />;
  }

  if (medewerkerVestigingenRequest.error) {
    return <ErrorPage error={medewerkerVestigingenRequest.error} data={medewerkerVestigingenRequest.data} />;
  }

  if (organisatieVestigingenRequest.error) {
    return <ErrorPage error={organisatieVestigingenRequest.error} data={organisatieVestigingenRequest.data} />;
  }

  if (
    typeof medewerkerRequest.data === "string" ||
    !medewerkerRequest.data?.isValid ||
    typeof medewerkerVestigingenRequest.data === "string" ||
    !medewerkerVestigingenRequest.data?.isValid ||
    typeof rechtenRequest.data === "string" ||
    !rechtenRequest.data?.isValid ||
    typeof organisatieVestigingenRequest.data === "string" ||
    !organisatieVestigingenRequest.data?.isValid ||
    !medewerkerId
  ) {
    return <ErrorPage error={Error("Communicatie fout, neem contact op met klantenservice")} data={null} />;
  }

  if (rechten && !rechten.some(recht => recht === "ADV")) {
    return <ErrorPage error={{ ...Error("Geen rechten toegekend voor deze gebruiker"), status: 403 }} data={null} />;
  }

  const organisatieVestigingenIds = organisatieVestigingen && Object.keys(organisatieVestigingen);
  const vestigingenIds = medewerkerVestigingen && Object.keys(medewerkerVestigingen);

  const notificatieChildren: ReactElement = (
    <>
      {/* istanbul ignore next */}
      {featureIntercom && (
        <IntercomBoot
          intercomAppId="f5h77p2u"
          intercomJwt={intercomToken || ""}
          intercomBrandId="1916"
          verticalPadding={78}
          horizontalPadding={12}
        />
      )}

    <UpscopeProvider apiKey={settings.upscopeApiKey} enabled={!!featureUpscope}>
      <OpenDossierLogProvider>
        <DashboardDataProvider>
          <DossierAanvraagFoutenProvider>
            <Switch>
              <Route path="/components" component={withErrorBoundary(ComponentsOverview)} />
              <Route path="/users/:id" component={withErrorBoundary(ComponentsOverview)} />
              <Route path="/vestiging/:vestiging/zoeken" component={withErrorBoundary(ZoekschermAjax)} />
              <Route
                path="/vestiging/:vestiging/import/:koppelingKey/taak/:importTaakId"
                render={() => (
                  <SemaphoreProvider maxConcurrent={3}>
                    <ExterneKoppelingImportAjax />
                  </SemaphoreProvider>
                )}
              />
              <Route
                path="/vestiging/:vestiging/adviesdossier/:adviesdossier/voorstel/:voorstel"
                render={
                  /* istanbul ignore next */ (): ReactElement => (
                    <AuthenticatedAdviesBoxAppWithDrawer
                      gebruikersVestigingIds={vestigingenIds || undefined}
                      organisatieVestigingIds={organisatieVestigingenIds || undefined}
                    />
                  )
                }
              />
              <Route
                path="/vestiging/:vestiging/adviesdossier/:adviesdossier"
                render={
                  /* istanbul ignore next */ (): ReactElement => (
                    <AuthenticatedAdviesBoxAppWithDrawer
                      gebruikersVestigingIds={vestigingenIds || undefined}
                      organisatieVestigingIds={organisatieVestigingenIds || undefined}
                    />
                  )
                }
              />

              <Route
                exact
                path={"/vestiging/:vestiging"}
                render={
                  /* istanbul ignore next */ ({ history }: RouteComponentProps<RouteParams>) => {
                    const base = getPathname().charAt(getPathname().length - 1);
                    if (base === "/") {
                      history.push(`${getPathname()}zoeken`);
                      return <></>;
                    }
                    history.push(`${getPathname()}/zoeken`);
                    return <></>;
                  }
                }
              />

              <Route
                exact
                path={"/"}
                render={
                  /* istanbul ignore next */ ({ history }: RouteComponentProps) => {
                    if (!vestigingIdCookie) {
                      window.location.assign(linkToPortal);
                      return <></>;
                    }

                    history.push(`/vestiging/${vestigingIdCookie}/zoeken`);
                    return <></>;
                  }
                }
              />

              <Route>
                <HttpErrorPage status={404} returnUrl={linkToPortal} />
              </Route>
            </Switch>
          </DossierAanvraagFoutenProvider>
        </DashboardDataProvider>
      </OpenDossierLogProvider>
    </UpscopeProvider>
    </>
  );

  return (
    <div data-testid="authenticated-app" className="adviesbox-advies">
      {featureNotificaties2 && (
        <Notificaties2Provider sourceApp="ADV" vestigingId={vestigingId}>
          {notificatieChildren}
        </Notificaties2Provider>
      )}
      {!featureNotificaties2 && (
        <NotificatiesProvider
          reloadTimeInSeconds={60}
          medewerkerId={medewerkerId}
          sourceApp="ADV"
          vestigingId={vestigingId}
        >
          {notificatieChildren}
        </NotificatiesProvider>
      )}
    </div>
  );
};

AuthenticatedAdviesBoxApp.displayName = "AuthenticatedAdviesBoxApp";
export default AuthenticatedAdviesBoxApp;
